import "bootstrap-icons/font/bootstrap-icons.css";
import { Suspense, lazy, useState } from "react";
import { OverlayChildren } from "react-bootstrap/esm/Overlay";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { UserService } from "../../../components/account/user/userService";
import ProfileView from "../../../components/profile/accountView";
import { UserDto } from "../../../services/dto/account/userDto";
import { IActionResponse, IActionResult } from "../../../services/proxies";
import { ISideNavSettings, IsSideNavNotEmpty } from "../../configuration";
import Logo from "../../images/logo.png";
import { GRID_ACTION_TYPES } from "../../types";
import { apiErrorMessage } from "../../utils/lib/error";
import { MessageSeverity } from "../../utils/lib/messageSeverity";
import { LoadingIndicator } from "../../vfx/loadingIndicator/vfxLoadingIndicator";
import { useSnackbar } from "../../vfx/snackbar/vfxSnackbarContext";
import { ILayoutStyles } from "../index";
import { Profile } from "./profile";
import { ClaimHelper } from "../../utils/helpers";
import { Constants } from "../../utils/lib/constants";
import { UserChangePasswordDto } from "../../utils/lib/userChangePasswordDto";
const CommunityPicker = lazy(() => import("./communityPicker"));
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../stores/store";
import { IconToggleButton, ProfileTriggerButton, HeaderAvatar, LoginButton } from "./header.styled";

type HeaderProps = {
    applicationTitle?: string;
    style?: ILayoutStyles;
    sideNavSettings: ISideNavSettings
    isAuthenticated?: boolean;
    userDisplayName?: string;
    hasNewNotification?: boolean;
    handleSideNavToggle: Function;
    onLogin: Function;
    onLogout: Function;
    hideHeader?: boolean;
};

const ProfileForm = lazy(() =>
    import('../../../components/profile/accountForm')
        .then((module) => ({
            default: module.ProfileForm,
        }))
);

const ChangePasswordForm = lazy(() =>
    import('../../../components/profile/changePassowrd')
        .then((module) => ({
            default: module.ChangePassword,
        }))
);

export const Header = (props: HeaderProps) => {
    const [togglePopOver, setTogglePopOver] = useState<boolean>(false);
    const [drawerType, setDrawerType] = useState<string>(GRID_ACTION_TYPES.HIDDEN);
    const { status: saveStatus, mutate: saveUser } = UserService.save();
    const { status: changePasswordStatus, mutate: changePassword } = UserService.changePassword();
    const { showSnackbar } = useSnackbar();
    const [displayName, setDisplayName] = useState<string | undefined>(props.userDisplayName);
    const { isSingleCommunityUser: isSingleCommunityUserFromStore } = useSelector((state: RootState) => state.layout);
    const userInitials: string = ClaimHelper.getUserInitials();

    const handleSideNavToggleClick = () => props.handleSideNavToggle();

    const handleLoginClick = () => props.onLogin();

    const handleLogoutClick = () => props.onLogout();

    const handleViewProfileClick = () => {
        setDrawerType(GRID_ACTION_TYPES.VIEW);
        setTogglePopOver(false);
    };

    const handleChangePasswordClick = () => {
        setDrawerType("changepassword");
        setTogglePopOver(false);
    };

    const handleEditProfileClick = () => {
        setDrawerType(GRID_ACTION_TYPES.EDIT);
        setTogglePopOver(false);
    }

    const handleSubmit = (values: UserDto) => {
        saveUser(values, {
            onSuccess: (response: IActionResult<UserDto>) => handleResponse(response, "Profile saved successfully", () => {
                ClaimHelper.updateProfileDisplayName(values.firstName ?? Constants.emptyString, values.lastName ?? Constants.emptyString);
                ClaimHelper.updateProfilePhoneNumber(values.phoneNumber);
                setDisplayName(ClaimHelper.getDisplayName());
            })
        });
    };

    const handleChangePasswordSubmit = (values: UserChangePasswordDto) => {
        values.userId = ClaimHelper.getUserId();
        changePassword(values, { onSuccess: (response: IActionResult<UserChangePasswordDto>) => handleResponse(response, "User password changed successfully") });
    }

    function handleResponse(response: IActionResponse, snackbarMessage: string, callbackfn?: Function) {
        if (response) {
            if (response.isSuccess) {
                showSnackbar(snackbarMessage, MessageSeverity.Success);
                callbackfn?.();
                closeDrawer();
            } else {
                if (response.errorMessages) {
                    const errorMsg: string | null = apiErrorMessage(response.errorMessages);
                    snackbarMessage = errorMsg ?? "Error";
                }
                showSnackbar(snackbarMessage, MessageSeverity.Error);
            }
        }
        else {
            showSnackbar("Error, unable to process your request please try again later.", MessageSeverity.Error);
        }
    }

    const popover: OverlayChildren = (
        <Popover id="profile-popover" style={props.style?.profile?.profileContainer} >
            <Profile style={props.style?.profile} handleLogOutClick={handleLogoutClick} handleViewProfileClick={handleViewProfileClick} handleChangePasswordClick={handleChangePasswordClick} />
        </Popover>
    );

    function GetContainerClass(): string {

        if (props.hideHeader ?? true) {
            return "d-none";
        }

        return "w-100 m-0 align-content-center";
    };

    const closeDrawer = () => setDrawerType(GRID_ACTION_TYPES.HIDDEN);

    function renderDrawerContent() {
        switch (drawerType) {
            case GRID_ACTION_TYPES.EDIT: {
                return (
                    <Suspense fallback={<LoadingIndicator isFullPage={true} />}>
                        <ProfileForm show onSubmit={handleSubmit} onHide={closeDrawer} />
                    </Suspense>
                )
            }
            case GRID_ACTION_TYPES.VIEW:
                return <ProfileView show onEdit={handleEditProfileClick} onHide={closeDrawer} />
            case "changepassword":
                return (
                    <Suspense fallback={<LoadingIndicator isFullPage={true} />}>
                        <ChangePasswordForm show onSubmit={handleChangePasswordSubmit} onHide={closeDrawer} />
                    </Suspense>
                )
            default:
                return null;
        }
    }
    return (
        <div className={GetContainerClass()} style={props.style?.header?.header}>
            <div className="container-fluid d-flex justify-content-between ps-0 pe-4 mx-0">
                <div className="d-flex align-items-center ms-3">
                    {IsSideNavNotEmpty(props.sideNavSettings) && (
                        <IconToggleButton
                            type="button"
                            aria-label="Toggle navigation"
                            className="bi bi-list me-4"
                            onClick={handleSideNavToggleClick}
                        />
                    )}

                    <NavLink to="/herenow/reference" end className="d-inline-block" aria-label="Home">
                        <img src={Logo} alt="" className="img-fluid" style={props.style?.header?.logo} />
                    </NavLink>

                    {props.applicationTitle && (
                        <NavLink
                            to="/herenow/reference"
                            end
                            className="ps-5"
                            style={props.style?.header?.headerItem}
                        >
                            {props.applicationTitle}
                        </NavLink>
                    )}
                </div>

                <div className="d-none d-md-block d-flex align-items-center align-self-center" style={props.style?.header?.headerContainer}>
                    {props.isAuthenticated ? (
                        <div className="d-flex align-items-center">
                            <div className="mx-5">
                                <Suspense fallback={null}>
                                    <CommunityPicker />
                                </Suspense>
                            </div>
                            {
                                !isSingleCommunityUserFromStore &&
                                <span className="bi bi-bell-fill position-relative me-3" style={props.style?.header?.headerIcon} aria-hidden="true">
                                    {props.hasNewNotification &&
                                        <span className="position-absolute top-0 start-100 translate-middle p-2 bg-danger border border-light rounded-circle">
                                            <span className="visually-hidden">You have new notifications</span>
                                        </span>
                                    }
                                </span>
                            }
                            <OverlayTrigger trigger="click" rootClose placement="bottom-start" show={togglePopOver} onToggle={setTogglePopOver} overlay={popover}>
                                <ProfileTriggerButton type="button" aria-label="Open profile menu" aria-haspopup="true" aria-expanded={togglePopOver}>
                                    <HeaderAvatar>{userInitials}</HeaderAvatar>
                                    <span>{displayName}</span>
                                </ProfileTriggerButton>
                            </OverlayTrigger>
                        </div>
                    ) : (
                        <LoginButton type="button" onClick={handleLoginClick}>
                            Login
                        </LoginButton>
                    )}
                </div>
                {renderDrawerContent()}
            </div>

        </div>

    );
};